import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

import type { GetInputOptionsAndLabelFunc, GetSelectOrAutocompleteDisplayValueFunc } from 'app/components';
import { SubmitConfirmationDialog } from 'app/components';
import ActionButton from 'app/components/ActionButton';
import { EmployeeDetailsPanelHeaderWithSyncToggle } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeDetailsPanelHeaderWithSyncToggle';
import type { EmployeeDetailsPanelFormValues } from 'app/Domain/Employees/Pages/EmployeeDetailPage/components/EmployeeInfoTab/components/EmployeeDetailsPanel/EmployeeDetailsPanel.types';
import { TranslationKeys } from 'app/translations';
import type { UseQueryRefetch } from 'app/types';

import { DivEditButtonsContainer } from './EmployeeDetailsPanelForm.styles';

type EmployeeDetailsPanelActionsProps = {
  isEdit: boolean;
  onClickEditButton: () => void;
  onClickCancelButton: () => void;
  onClickSubmit: () => void;
  disabledActionButtonsWhileLoading: boolean;
  synchronizationStatus: boolean | undefined;
  disabledIfNoAccessToEmployeeEdit: boolean;
  disabledIfNoAccessToConnectionRun: boolean;
};

export const EmployeeDetailsPanelActions = ({
  isEdit,
  onClickEditButton,
  onClickCancelButton,
  onClickSubmit,
  disabledActionButtonsWhileLoading,
  synchronizationStatus,
  disabledIfNoAccessToEmployeeEdit,
  disabledIfNoAccessToConnectionRun,
}: EmployeeDetailsPanelActionsProps) => (
  <>
    {!isEdit && (
      <ActionButton
        messageId={TranslationKeys.global_edit}
        variant="text"
        icon={faPenToSquare}
        onClick={onClickEditButton}
        disabled={
          disabledActionButtonsWhileLoading ||
          synchronizationStatus ||
          disabledIfNoAccessToEmployeeEdit ||
          disabledIfNoAccessToConnectionRun
        }
      />
    )}
    {isEdit && (
      <DivEditButtonsContainer>
        <ActionButton
          messageId={TranslationKeys.button_cancel}
          onClick={onClickCancelButton}
          variant="outlined"
          disabled={
            disabledActionButtonsWhileLoading || disabledIfNoAccessToEmployeeEdit || disabledIfNoAccessToConnectionRun
          }
        />
        <ActionButton
          messageId={TranslationKeys.button_save}
          type="submit"
          onClick={onClickSubmit}
          disabled={
            disabledActionButtonsWhileLoading || disabledIfNoAccessToEmployeeEdit || disabledIfNoAccessToConnectionRun
          }
        />
      </DivEditButtonsContainer>
    )}
  </>
);

type EmployeeDetailsPanelHeaderProps = {
  loading: boolean;
  synchronizationStatus: boolean | undefined;
  refetchGetEmployeeSynchronizationQuery: UseQueryRefetch;
  disabledActionButtonsWhileLoading: boolean;
  employeeId: string | undefined;
  connectionId: string | undefined;
  deletedBySourceAddress?: boolean;
  deletedBySourcePersonal?: boolean;
};

export const EmployeeDetailsPanelHeader = ({
  loading,
  synchronizationStatus,
  refetchGetEmployeeSynchronizationQuery,
  disabledActionButtonsWhileLoading,
  employeeId,
  connectionId,
  deletedBySourceAddress,
  deletedBySourcePersonal,
}: EmployeeDetailsPanelHeaderProps) => (
  <EmployeeDetailsPanelHeaderWithSyncToggle
    field="employee"
    loading={loading}
    synchronizationStatus={synchronizationStatus}
    refetchGetEmployeeSynchronizationQuery={refetchGetEmployeeSynchronizationQuery}
    disabledActionButtonsWhileLoading={disabledActionButtonsWhileLoading}
    messageId={TranslationKeys.employees_detail_personal}
    employeeId={employeeId}
    connectionId={connectionId}
    tooltips={[
      { show: !!deletedBySourcePersonal, messageId: 'employees.detail.employment.deletedBySourcePersonal' },
      { show: !!deletedBySourceAddress, messageId: 'employees.detail.employment.deletedBySourceAddress' },
    ]}
  />
);

type EmployeeDetailsPanelConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  previousValues: EmployeeDetailsPanelFormValues;
  newValues: EmployeeDetailsPanelFormValues;
  getSelectOrAutocompleteDisplayValue: GetSelectOrAutocompleteDisplayValueFunc;
  getInputOptionsAndLabel: GetInputOptionsAndLabelFunc;
};

export const EmployeeDetailsPanelConfirmationDialog = (props: EmployeeDetailsPanelConfirmationDialogProps) => (
  <SubmitConfirmationDialog {...props} />
);
